import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import dynamic from 'next/dynamic';
import { SetterOrUpdater, useSetRecoilState } from 'recoil';
import { GetServerSideProps, GetServerSidePropsContext, PreviewData } from 'next';
import { brandSelectorState, sustainableBgState } from '../../data/estimate/common/modal/atom';
import useSwitchingSelector from '../../hooks/estimate/common/useSwitchingSelector';
import { loadDataInfo, tabState } from '../../data/estimate/common/common';
import {
  BondDiscountRate,
  DefaultManager,
  Memo,
  DefaultSetting,
} from '../../types/estimate/common/Default';
import {
  bondDiscountRateListState,
  defaultSettingState,
  managerState,
  memoListState,
  userNoState,
} from '../../data/estimate/common/defaultSetting/atom';
import useReset from '../../hooks/common/useReset';
import EstimateHeader from '../../components/estimate/common/header/EstimateHeader';
import SelectCarTable from '../../components/estimate/common/selectCar/table/SelectCarTable';
import OptionTable from '../../components/estimate/common/optionAndSale/table/OptionTable';
import TaxTable from '../../components/estimate/common/tax/table/TaxTable';
import ConsignmentTable from '../../components/estimate/new/consignment/table/ConsignmentTable';
import IncidentalExpensesTable from '../../components/estimate/common/incidentalExpenses/table/IncidentalExpensesTable';
import PaymentWayTable from '../../components/estimate/common/paymentWay/table/PaymentWayTable';
import PaymentChargeTable from '../../components/estimate/common/paymentCharge/table/PaymentChargeTable';
import CustomerTable from '../../components/estimate/common/customer/table/CustomerTable';
import MemoRecordWrapper from '../../components/estimate/common/memoRecord/MemoRecordWrapper';
import EstimateMarkTable from '../../components/estimate/common/estimateMark/table/EstimateMarkTable';
import OverallBar from '../../components/estimate/common/overallBar/OverallBar';
import { ParsedUrlQuery } from 'querystring';
import useDevice from '../../hooks/common/useDevice';
import CompareOtherBankButton from '../../components/estimate/common/overallBar/button/CompareOtherBankButton';
import QuickReleaseCarButton from '../../components/estimate/common/overallBar/button/QuickReleaseCarButton';
import FixedRightMobileButton from '../../components/estimate/common/overallBar/button/FixedRightMobileButton';
import LoadArea from '../../components/estimate/common/LoadArea';
import Loading from '../../components/common/Loading';
import { useRouter } from 'next/router';

const CompareTableArea = dynamic(
  import('../../components/estimate/common/paymentWay/area/CompareTableArea')
);
const OtherBankArea = dynamic(
  import('../../components/estimate/common/otherBank/area/OtherBankArea')
);

const Wrapper = styled.div<{ isLoading: boolean }>`
  display: ${props => (props.isLoading ? 'none' : 'grid')};
  grid-template-columns: repeat(2, auto);
  gap: 8px;
  width: 1024px;
  height: auto;
  padding: 0 20px;
  overflow-x: overlay;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    width: auto;
    padding: 0;
    overflow-x: hidden;
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: repeat(6, auto);
  gap: 8px;

  & > table:first-of-type,
  & > table:nth-of-type(2),
  & > table:nth-of-type(3),
  & > table:nth-of-type(6),
  & > table:nth-of-type(7),
  & > table:nth-of-type(8),
  & > table:nth-of-type(9),
  & > table:nth-of-type(10),
  & > table:last-of-type {
    grid-column: span 2;
  }

  & > table:nth-of-type(4),
  & > table:nth-of-type(5) {
    grid-column: span 3;
  }

  & > div:first-of-type {
    grid-column: span 6;
  }
  & > div:last-of-type {
    grid-column: span 2;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;

    & > table,
    & > div > table {
      width: 100%;
    }
  }
`;

interface Props {
  bondDiscountRateList: BondDiscountRate[];
  salesManagerInfo: DefaultManager;
  memoList: Memo[];
  defaultSetting: DefaultSetting;
  userNo: string;
  loadData: any;
  query: ParsedUrlQuery;
  isLocal: boolean;
}

/**
 * @param {BondDiscountRate[]} bondDiscountRateList 채권시세 목록
 * @param {Manager} salesManagerInfo 매니저 정보
 * @param {Memo[]} memoList 메모 목록
 * @param {DefaultSetting} defaultSetting 초기 설정값
 * @param {string} userNo 유저 번호
 * @param {any} loadData 불러온 데이터 (uri에 query parameter newEstimateNo 가 있을 때)
 * @param {ParsedUrlQuery} query query
 * @param {boolean} isLocal 로컬환경 여부
 *
 */
function NewPage({
  bondDiscountRateList,
  salesManagerInfo,
  memoList,
  defaultSetting,
  userNo,
  loadData,
  query,
  isLocal,
}: Props): JSX.Element {
  const router = useRouter();
  // device
  const device: string = useDevice();

  // state
  const [loading, setLoading] = useState<boolean>(true);

  // reset
  useReset(tabState);

  // set
  const setDiscountRateList: SetterOrUpdater<BondDiscountRate[]> =
    useSetRecoilState(bondDiscountRateListState);
  const setManager: SetterOrUpdater<DefaultManager> = useSetRecoilState(managerState);
  const setMemoList: SetterOrUpdater<Memo[]> = useSetRecoilState(memoListState);
  const setDefaultSetting: SetterOrUpdater<DefaultSetting> = useSetRecoilState(defaultSettingState);
  const setUserNo: SetterOrUpdater<string> = useSetRecoilState(userNoState);
  const setLoadData: SetterOrUpdater<any> = useSetRecoilState(loadDataInfo);
  // const setAlert: SetterOrUpdater<Alert> = useSetRecoilState(alertState);

  // function
  const switchingSelector: (firstParam?: boolean, secondParam?: boolean) => void =
    useSwitchingSelector({
      fisrtSelector: brandSelectorState,
      secondSelector: sustainableBgState,
    });

  useEffect(() => {
    // if (brandList?.length) {
    //   console.log('백엔드 => ', response);
    //   !query.newEstimateNo &&
    //     setTimeout(() => {
    //       switchingSelector(true, true);
    //     }, 500);
    // } else {
    //   alert('브랜드 목록을 가져올 수 없습니다.');
    //   // setAlert(
    //   //   (prev: Alert) =>
    //   //     prev && { ...prev, active: true, contents: '브랜드 목록을 가져올 수 없습니다.' }
    //   // );
    // }

    !query.newEstimateNo &&
      setTimeout(() => {
        //switchingSelector(true, true);
        switchingSelector(true, true);
      }, 500);

    setDiscountRateList(bondDiscountRateList);
    setManager(salesManagerInfo);
    setMemoList(memoList);
    setDefaultSetting(defaultSetting);

    userNo || isLocal ? setUserNo(userNo) : router.replace('/');
    loadData ? setLoadData(loadData) : setLoading(false);

    if (window.location.hostname !== 'localhost') {
      // 새로고침 막기(조건 부여 가능)
      window.onbeforeunload = function () {
        // const yesData: boolean = !!infos.filter((data: SelectInfo<Brand>) => data.info).length;
        return true;
      };
      return () => {
        window.onbeforeunload = null;
      };
    }
  }, []);

  return (
    <Wrapper isLoading={false}>
      <EstimateHeader type='newcar' query={query}/>
      <Body>
        <SelectCarTable type='newcar' />
        <OptionTable optionType='option' type='newcar' />
        <OptionTable optionType='sale' type='newcar' />
        <TaxTable type='newcar' />
        <ConsignmentTable type='newcar' />
        <IncidentalExpensesTable type='newcar' />
        <PaymentWayTable tableNo={1} />
        {device === 'mobile' && <CompareOtherBankButton />}
        <PaymentChargeTable />
        <CompareTableArea />
        <CustomerTable />
        <MemoRecordWrapper />
        <EstimateMarkTable type='newcar' />
        <OtherBankArea />
      </Body>
      <OverallBar type='newcar' device={device} />
      {device === 'mobile' && (
        <>
          <QuickReleaseCarButton type='newcar' query={query} isMobile />
          <FixedRightMobileButton type='newcar' />
        </>
      )}
      <LoadArea
        type='newcar'
        isPrice={query?.price === ''}
        isSpec={query?.spec === ''}
        setLoading={setLoading}
      />
      {loading && <Loading />}
    </Wrapper>
  );
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>
) => {
  const Cookie: string = context?.req?.headers?.cookie || '';
  const { req, query } = context;
  const host: string = req.headers.host || '';
  const requestOrigin: string = `https://${host?.includes('test') ? 'test.' : ''}solecar.co.kr`;
  //console.log( '%o', process.env.NEXT_PUBLIC_API_SOLECAR);

  // // 브랜드 목록 요청
  // const brandUrl: string = req.headers.host
  //   ? `${requestOrigin}/v2/newcar/api/brand${
  //       host?.includes('localhost') ||
  //       host?.includes('192.168') ||
  //       host?.includes(process.env.NEXT_PUBLIC_LOCAL_IP_ADDRESS || '')
  //         ? 'Local'
  //         : ''
  //     }`
  //   : '';
  // const brandResponse: AxiosResponse = await axios.get(brandUrl);
  // const brandList: Brand[] = brandResponse?.data?.brandList || [];
  // const brandList: Brand[] = sortArray<Brand>(brandResponse.data.brandList, 'sortOrder');

  // 채권시세 목록 요청
  const bondDiscountRate: AxiosResponse = await axios.get(
    `${requestOrigin}/newcar/api/get/bondDiscountRate`
  );
  const bondDiscountRateList: BondDiscountRate[] = bondDiscountRate?.data || [];

  // 매니저 정보 요청
  const getSalesManager: AxiosResponse = await axios.get(
    `${requestOrigin}/user_info/get_sales_manager`,
    { headers: { Cookie } }
  );
  const salesManagerInfo: DefaultManager = getSalesManager?.data;

  // 메모 목록 요청
  const getMemoList: AxiosResponse = await axios.get(`${requestOrigin}/user_info/get_memo_list`, {
    headers: { Cookie },
  });
  const memoList: Memo[] = getMemoList?.data || [];

  // 초기 사용 설정 요청
  const getEstimateDefault: AxiosResponse = await axios.get(
    `${requestOrigin}/get_estimate_default`,
    { headers: { Cookie } }
  );
  const defaultSetting: DefaultSetting = getEstimateDefault?.data;

  // 유저 번호 정보 요청
  const getUserNo: AxiosResponse = await axios.get(`${requestOrigin}/user_info/get/userName`, {
    headers: { Cookie },
  });
  const userNo: string = getUserNo?.data?.userName || '';

  const isExistPrice: string | string[] | undefined = query.price

  console.log(isExistPrice);

  // 견적 불러오기 요청
  const getLoadData: AxiosResponse | null = query.newEstimateNo
    ? await axios.get(
        `${requestOrigin}/newcar/api/get/estimate?newEstimateNo=${query.newEstimateNo}`,
        {
          headers: { Cookie },
        }
      )
    : null;
  const loadData: any = getLoadData?.data || null;
  const isLocal: boolean = host.includes('localhost');
  return {
    props: {
      bondDiscountRateList,
      salesManagerInfo,
      memoList,
      defaultSetting,
      userNo,
      loadData,
      query,
      isLocal,
    },
  };
};

export default NewPage;
